<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, useTemplateRef } from 'vue';
// biome-ignore lint/correctness/noUnusedImports: used in <template>
import KioskLayout from '@/app/layouts/kiosk/KioskLayout.vue';
// biome-ignore lint/correctness/noUnusedImports: used in <template>
import KioskStepProgressIndicator from '@/app/layouts/kiosk/KioskStepProgressIndicator.vue';
import { store } from '@/app/store';
import { resolvePortraitOrientation } from '@/shared/lib/portrait-orientation';
// biome-ignore lint/correctness/noUnusedImports: used in <template>
import PrintingRecoveryActions from './printing/PrintingRecoveryActions.vue';
// biome-ignore lint/correctness/noUnusedImports: used in <template>
import PrintQueueGallery, {
  type PrintQueueGalleryApi,
} from './printing/PrintQueueGallery.vue';
import {
  type PrintingQueueItem,
  usePrintingFlow,
} from './printing/usePrintingFlow';

const gallery = useTemplateRef<PrintQueueGalleryApi>('gallery');
// biome-ignore lint/correctness/noUnusedVariables: used in <template>
const printingQueue = computed(
  () => store.printingQueue as PrintingQueueItem[]
);
const isVerticalKiosk = computed(() => Boolean(store.isVerticalKiosk));
// biome-ignore lint/correctness/noUnusedVariables: used in <template>
const isLandscapeKiosk = computed(() => !store.viewportIsPortrait);
// biome-ignore lint/correctness/noUnusedVariables: used in <template>
const useImageGrid = computed(
  () =>
    isVerticalKiosk.value &&
    resolvePortraitOrientation(
      store.kiosk as unknown as { portrait_orientation?: unknown }
    ) === 'horizontal'
);

const printingFlow = usePrintingFlow({
  scrollToNextSlideIfNeeded: () =>
    gallery.value?.scrollToNextPrintedItemIfNeeded(),
});
// biome-ignore lint/correctness/noUnusedVariables: used in <template>
const errorSettled = computed(
  () =>
    store.printingHasErrors &&
    !store.printingInProgress &&
    !printingFlow.isRetrying.value
);

onMounted(() => {
  printingFlow.enter();
});

onBeforeUnmount(() => {
  printingFlow.leave();
});
</script>

<template>
  <KioskLayout>
    <template #KioskLayout__bottom>
      <KioskStepProgressIndicator :index="3" />
    </template>

    <div
      class="Printing"
      :class="{
        'Printing--error': errorSettled,
        'Printing--has-errors': store.printingHasErrors,
        'Printing--horizontal-grid': useImageGrid,
        'Printing--single-item': printingQueue.length === 1,
        'Printing--vertical': isVerticalKiosk,
      }"
      data-testid="printing-root"
    >
      <h1 v-if="store.printingHasErrors" class="Printing__title kiosk-title">
        {{ $t('Printing.titleError') }}
      </h1>
      <h1 v-else class="Printing__title kiosk-title">
        {{ $t('Printing.title') }}
      </h1>

      <i
        v-if="store.printingInProgress"
        class="Printing__page-spinner kiosk-spinner"
      />

      <PrintQueueGallery
        ref="gallery"
        :error-settled="errorSettled"
        :has-errors="store.printingHasErrors"
        :is-landscape-kiosk="isLandscapeKiosk"
        :is-vertical-kiosk="isVerticalKiosk"
        :items="printingQueue"
        :use-image-grid="useImageGrid"
      />

      <PrintingRecoveryActions
        :visible="errorSettled"
        @continue-without-print="printingFlow.onContinueWithoutPrint"
        @retry="printingFlow.onFixErrorsClick"
      />
    </div>
  </KioskLayout>
</template>

<style scoped lang="scss">
.Printing {
  position: relative;
}

.Printing--horizontal-grid {
  width: calc(
    100vw - var(--Kiosk--wrapper-side-gap) - var(--Kiosk--wrapper-side-gap)
  );
  max-width: 100%;
  min-width: 0;
}

.Printing__title {
  margin-bottom: 1rem;
}

.Printing--horizontal-grid .Printing__title {
  width: 100%;
  max-width: 100%;
  white-space: normal;
}

.Printing__page-spinner {
  display: none;
}

.Printing--vertical .Printing__page-spinner {
  --kiosk-spinner-size: 1.85rem;
  --kiosk-spinner-color: #ffffff;
  --kiosk-spinner-opacity: 0.8;
  --kiosk-spinner-thinkness: 0.17;

  display: block;
  position: absolute;
  top: 10.5rem;
  left: 50%;
  margin: 0;
  transform: translateX(-50%);
  z-index: 2;
}
</style>
